import React, { useState } from "react";
import ReportProfileSelector from "./ReportProfileSelector";
import ReportProfileTable from "./table/ReportProfileTable";
import useProfileReport from "../../../hooks/profile/useProfileReport";

/**
 * 📊 ReportProfilePanel
 *
 * Panel principal para administrar la configuración
 * de reportes habilitados por perfil de cliente.
 *
 * - Se apoya 100% en el hook `useProfileReport`.
 * - El hook maneja el backend, loading y alertas.
 * - Este componente solo se encarga de renderizar.
 */
const ReportProfilePanel: React.FC = () => {
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  const { reports, fetchReportsByProfile, toggleReport } = useProfileReport();

  /** 🔍 Buscar reportes reales por perfil */
  const handleSearchReports = async (profileId: number) => {
    // console.log("🔍 Buscar reportes reales para el perfil:", profileId);
    setSelectedProfileId(profileId);
    await fetchReportsByProfile(profileId);
  };

  /** ⚙️ Alternar el estado de un reporte */
  const handleToggleReport = async (report: { reportId: number; enabled: boolean }) => {
    if (!selectedProfileId) return;

    // 👁️ Construimos el payload completo
    const payload = {
      profileId: selectedProfileId,
      reportId: report.reportId,
      enable: !report.enabled, // alterna estado
    };

    // 🚀 Enviar al hook (que maneja backend y Redux)
    await toggleReport(payload);
  };

  return (
    <div className="rounded-md border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
      {/* 🔹 Selector de perfil */}
      <ReportProfileSelector onSearchReports={handleSearchReports} />

      {/* 🧾 Tabla visible solo si hay un perfil seleccionado */}
      {selectedProfileId && (
        <div className="mt-8">
          <h2 className="mb-4 text-xl font-semibold text-black dark:text-white">
            Configuración de Reportes
          </h2>

          <ReportProfileTable
            reports={reports}
            onToggle={(report) => handleToggleReport(report)} // ✅ pasa el objeto completo
          />
        </div>
      )}
    </div>
  );
};

export default ReportProfilePanel;
